/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import VmItemForm from '@web/src/features/vendor-market/vm-item-form/vm-item-form'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  CreateVendorMarketItemInput,
  CreateVendorMarketItemMutation,
  CreateVendorMarketItemMutationVariables,
} from 'types/graphql'

const CREATE_ITEM_MUTATION: TypedDocumentNode<CreateVendorMarketItemMutation, CreateVendorMarketItemMutationVariables> =
  gql`
  mutation CreateVendorMarketItem($input: CreateVendorMarketItemInput!) {
    createVendorMarketItem(input: $input) {
      id
      vendorMarketId
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/vendor-market/$vendorMarketIdOrSlug_/item/create')({
  component: CreateItemPage,
  head: () =>
    seo({
      title: `Add Item | ${env.VITE_APP_NAME}`,
      description: 'Add a new item to your vendor',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
})

function CreateItemPage() {
  const { t } = useTranslation()
  const { vendorMarketIdOrSlug } = Route.useParams()
  const [showSpinner, setShowSpinner] = useState(false)
  const navigate = useNavigate()

  const [createItem] = useMutation(CREATE_ITEM_MUTATION, {
    onCompleted: (result) => {
      toast.success(t('Item Created'))
      navigate({
        to: '/vendor-market/$vendorMarketIdOrSlug/item/$itemId',
        params: { vendorMarketIdOrSlug, itemId: result.createVendorMarketItem.id } as never,
      })
      setShowSpinner(false)
    },
    onError: (err) => {
      toast.error(err.message)
      setShowSpinner(false)
    },
  })

  const onSave = (input: CreateVendorMarketItemInput) => {
    setShowSpinner(true)
    createItem({ variables: { input } })
  }

  return (
    <div className="mx-auto w-full max-w-2xl pt-4">
      <div className="flex w-full flex-col items-stretch gap-4">
        <h1 className="w-full text-center font-bold text-4xl">{t('Add Item')}</h1>
        <VmItemForm onSave={onSave} showSpinner={showSpinner} vendorMarketId={vendorMarketIdOrSlug} />
      </div>
    </div>
  )
}
